import { useEffect, useState } from "react";
import { CalendarClock, Gift, Loader2, PiggyBank, Scale, TrendingUp, Wallet2 } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "../api";
import { useLanguage } from "../i18n";

const eur = (v, lang) => {
  try { return new Intl.NumberFormat(lang, { style: "currency", currency: "EUR" }).format(v || 0); }
  catch { return `€${(v || 0).toFixed(2)}`; }
};

export default function AdminMargins() {
  const { t, lang } = useLanguage();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/admin/margins").then(({ data }) => setData(data.data)).catch(() => setErr(true));
  }, []);

  if (err) return <p data-testid="margins-error" className="mt-10 text-left text-sm text-slate-400">{t("misc.error")}</p>;
  if (!data) return <div className="py-16 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[#FF6B35]" /></div>;

  const T = data.totals;
  const F = data.forecast;
  const since = data.since ? new Date(data.since).toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" }) : "—";
  const marginPos = T.margin_eur >= 0;

  const kpis = [
    { id: "cash", icon: PiggyBank, label: t("mrg.cashIn"), value: eur(T.cash_in_eur, lang), sub: t("mrg.cashInSub"), cls: "text-slate-900" },
    { id: "cost", icon: Scale, label: t("mrg.cost"), value: eur(T.cost_eur, lang), sub: t("mrg.costSub"), cls: "text-slate-900" },
    { id: "margin", icon: TrendingUp, label: t("mrg.margin"), value: eur(T.margin_eur, lang), sub: `${T.margin_pct}% · ${t("mrg.marginSub")}`, cls: marginPos ? "text-emerald-600" : "text-red-500" },
    { id: "wallet", icon: Wallet2, label: t("mrg.liability"), value: eur(data.wallet.liability_eur, lang), sub: `+${eur(data.wallet.potential_margin_eur, lang)} ${t("mrg.potential")}`, cls: "text-slate-900" },
    { id: "promo", icon: Gift, label: t("mrg.promo"), value: eur(data.promo?.bonus_codes_eur, lang), sub: t("mrg.promoSub").replace("{n}", data.promo?.bonus_codes_count ?? 0).replace("{r}", eur(data.promo?.referral_bonus_eur, lang)), cls: "text-slate-900" },
  ];

  const forecasts = [
    ["m1", t("mrg.m1"), F.m1], ["m3", t("mrg.m3"), F.m3], ["m6", t("mrg.m6"), F.m6],
    ["y1", t("mrg.y1"), F.y1], ["eoy", t("mrg.eoy"), F.eoy],
  ];

  const kindLabel = { card: t("mrg.card"), wallet: t("mrg.wallet"), wallet_topup: t("mrg.wtopup") };

  return (
    <div data-testid="admin-margins-page" className="mt-10 space-y-6">
      <div>
        <h2 className="font-display font-bold text-2xl text-slate-900">{t("admin.margins")}</h2>
        <p className="text-sm text-slate-500">{t("mrg.since")} {since} · {t("mrg.estimate").replace("{m}", data.markup_pct)}</p>
      </div>

      {/* KPI */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="margins-kpis">
        {kpis.map((k) => (
          <div key={k.id} data-testid={`margins-kpi-${k.id}`} className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6">
            <div className="flex items-center gap-2 font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
              <k.icon className="w-4 h-4 text-[#FF6B35]" /> {k.label}
            </div>
            <div className={`mt-3 font-display font-extrabold text-3xl ${k.cls}`}>{k.value}</div>
            <div className="mt-1 text-xs text-slate-400">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Previsioni */}
      <div className="rounded-3xl bg-[#1E293B] text-white p-6 sm:p-8" data-testid="margins-forecast">
        <div className="flex items-center gap-2 font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
          <CalendarClock className="w-4 h-4 text-[#FF6B35]" /> {t("mrg.forecast")}
        </div>
        <p className="mt-1 text-xs text-slate-400">{t("mrg.forecastSub").replace("{v}", eur(F.avg_daily_margin, lang))}</p>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-3">
          {forecasts.map(([id, label, val]) => (
            <div key={id} data-testid={`margins-fc-${id}`} className="rounded-2xl bg-white/5 border border-white/10 p-4">
              <div className="font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400">{label}</div>
              <div className={`mt-2 font-display font-bold text-xl ${val >= 0 ? "text-emerald-400" : "text-red-400"}`}>{eur(val, lang)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Grafico giornaliero */}
      {data.daily.length > 0 && (
        <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6" data-testid="margins-chart">
          <h3 className="font-display font-bold text-lg text-slate-900 mb-4">{t("mrg.daily")}</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.daily} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="mrgM" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6B35" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#FF6B35" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} stroke="#CBD5E1" />
                <YAxis tick={{ fontSize: 10 }} stroke="#CBD5E1" width={50} />
                <Tooltip formatter={(v, name) => [eur(v, lang), name === "margin" ? t("mrg.margin") : name === "cash_in" ? t("mrg.cashIn") : t("mrg.cost")]} />
                <Area type="monotone" dataKey="margin" stroke="#FF6B35" fill="url(#mrgM)" strokeWidth={2} />
                <Area type="monotone" dataKey="cash_in" stroke="#1E293B" fill="none" strokeWidth={1.5} strokeDasharray="4 3" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Operazioni */}
      <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6 overflow-x-auto" data-testid="margins-ops">
        <h3 className="font-display font-bold text-lg text-slate-900 mb-4">{t("mrg.ops")}</h3>
        {data.operations.length === 0 ? (
          <p data-testid="margins-ops-empty" className="py-8 text-left text-sm text-slate-400">{t("mrg.empty")}</p>
        ) : (
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="text-left font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100">
                <th className="pb-2 pr-3">{t("mrg.colWhen")}</th>
                <th className="pb-2 pr-3">{t("mrg.colDesc")}</th>
                <th className="pb-2 pr-3">{t("mrg.colType")}</th>
                <th className="pb-2 pr-3 text-right">{t("mrg.colRev")}</th>
                <th className="pb-2 pr-3 text-right">{t("mrg.colCost")}</th>
                <th className="pb-2 text-right">{t("mrg.colMargin")}</th>
              </tr>
            </thead>
            <tbody>
              {data.operations.map((o) => (
                <tr key={o.order_id} data-testid={`margins-op-${o.order_id}`} className="border-b border-slate-50 hover:bg-[#FAF5EE] transition-colors">
                  <td className="py-2.5 pr-3 font-mono-t text-xs text-slate-500 whitespace-nowrap">
                    {new Date(o.created_at).toLocaleDateString(lang, { day: "2-digit", month: "2-digit", year: "2-digit" })}
                  </td>
                  <td className="py-2.5 pr-3 text-slate-700 truncate max-w-[220px]">
                    {o.title || "—"} {o.destination && o.destination !== "Plafond" ? `· ${o.destination}` : ""}
                    {o.status === "refunded" && <span className="ml-2 rounded-full bg-red-50 text-red-500 px-2 py-0.5 font-mono-t text-[9px] uppercase tracking-widest">{t("mrg.refunded")}</span>}
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className={`rounded-full px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest ${o.kind === "card" ? "bg-sky-50 text-sky-700" : o.kind === "wallet" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {kindLabel[o.kind] || o.kind}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs">{eur(o.revenue_eur, lang)}</td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs text-slate-500">{o.cost_eur ? eur(o.cost_eur, lang) : "—"}</td>
                  <td className={`py-2.5 text-right font-mono-t text-xs font-bold ${o.margin_eur == null ? "text-slate-300" : o.margin_eur >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                    {o.margin_eur == null ? "—" : eur(o.margin_eur, lang)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
